import React, { useState } from 'react';
import API from '../../api';

function PasswordResetModal({ show, onHide, styles }) {
    const [passwordResetData, setPasswordResetData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
    const [passwordError, setPasswordError] = useState(null);
    const [success, setSuccess] = useState(null);

    if (!show) return null;

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setPasswordResetData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = () => {
        setPasswordError(null);
        setSuccess(null);

        if (!passwordResetData.currentPassword) { setPasswordError("Current password is required"); return; }
        if (!passwordResetData.newPassword) { setPasswordError("New password is required"); return; }
        if (passwordResetData.newPassword !== passwordResetData.confirmPassword) { setPasswordError("New passwords do not match"); return; }
        if (passwordResetData.newPassword.length < 12) { setPasswordError("Password must be at least 12 characters long"); return; }
        if (!/[A-Z]/.test(passwordResetData.newPassword)) { setPasswordError("Password must contain at least one uppercase letter"); return; }
        if (!/[a-z]/.test(passwordResetData.newPassword)) { setPasswordError("Password must contain at least one lowercase letter"); return; }
        if (!/[0-9]/.test(passwordResetData.newPassword)) { setPasswordError("Password must contain at least one number"); return; }
        if (!/[^A-Za-z0-9]/.test(passwordResetData.newPassword)) { setPasswordError("Password must contain at least one special character"); return; }

        fetch(API.RESET_PASSWORD, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ currentPassword: passwordResetData.currentPassword, newPassword: passwordResetData.newPassword })
        })
        .then(response => {
            if (!response.ok) return response.text().then(text => { throw new Error(`Server returned ${response.status}: ${text}`); });
            return response.text().then(text => { try { return JSON.parse(text); } catch (e) { throw new Error("Invalid JSON response from server"); } });
        })
        .then(data => {
            if (data.status === 'success') {
                setSuccess(data.message || "Password has been reset successfully");
                setPasswordResetData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                setTimeout(() => onHide(), 2000);
            } else {
                setPasswordError(data.message || "Failed to reset password");
            }
        })
        .catch(() => setPasswordError("An error occurred. Please try again."));
    };

    const getStrength = () => {
        const pw = passwordResetData.newPassword;
        if (pw.length < 8) return { label: 'Weak', cls: 'weak' };
        if (pw.length < 12) return { label: 'Medium', cls: 'medium' };
        if (/[A-Z]/.test(pw) && /[a-z]/.test(pw) && /[0-9]/.test(pw) && /[^A-Za-z0-9]/.test(pw)) return { label: 'Very Strong', cls: 'very-strong' };
        return { label: 'Strong', cls: 'strong' };
    };

    const strength = getStrength();

    return (
        <div className={styles['pwd-reset-modal']}>
            <div className={styles['pwd-reset-modal-content']}>
                <h3>Reset Password</h3>
                {passwordError && <div className={styles['pwd-reset-error-message']}>{passwordError}</div>}
                {success && <div className={styles['pwd-reset-success-message']}>{success}</div>}

                {!success ? (
                    <>
                        <div className={styles['pwd-reset-form-group']}>
                            <label htmlFor="currentPassword">Current Password:</label>
                            <input type="password" id="currentPassword" name="currentPassword" value={passwordResetData.currentPassword} onChange={handleInputChange} placeholder="Enter your current password" />
                        </div>
                        <div className={styles['pwd-reset-form-group']}>
                            <label htmlFor="newPassword">New Password:</label>
                            <input type="password" id="newPassword" name="newPassword" value={passwordResetData.newPassword} onChange={handleInputChange} placeholder="Enter your new password" />
                            {passwordResetData.newPassword && (
                                <>
                                    <div className={styles['pwd-strength-meter']}>
                                        <div className={`${styles['pwd-strength-meter-bar']} ${styles[strength.cls]}`}></div>
                                    </div>
                                    <div className={styles['pwd-strength-text']}>{strength.label}</div>
                                </>
                            )}
                        </div>
                        <div className={styles['pwd-reset-form-group']}>
                            <label htmlFor="confirmPassword">Confirm New Password:</label>
                            <input type="password" id="confirmPassword" name="confirmPassword" value={passwordResetData.confirmPassword} onChange={handleInputChange} placeholder="Confirm your new password" />
                            {passwordResetData.newPassword && passwordResetData.confirmPassword && passwordResetData.newPassword !== passwordResetData.confirmPassword && (
                                <div className={styles['pwd-reset-error-message']}>Passwords do not match</div>
                            )}
                        </div>
                        <div className={styles['pwd-reset-requirements']}>
                            <p>Password must:</p>
                            <ul>
                                <li>Be at least 12 characters long</li>
                                <li>Include at least one uppercase letter</li>
                                <li>Include at least one lowercase letter</li>
                                <li>Include at least one number</li>
                                <li>Include at least one special character</li>
                            </ul>
                        </div>
                        <div className={styles['pwd-reset-buttons']}>
                            <button onClick={handleSubmit} className={styles['pwd-reset-submit-button']}
                                disabled={!passwordResetData.currentPassword || !passwordResetData.newPassword || !passwordResetData.confirmPassword || passwordResetData.newPassword !== passwordResetData.confirmPassword}>
                                Reset Password
                            </button>
                            <button onClick={onHide} className={styles['pwd-reset-cancel-button']}>Cancel</button>
                        </div>
                    </>
                ) : (
                    <div className={styles['pwd-reset-buttons']}>
                        <button onClick={onHide} className={styles['pwd-reset-submit-button']}>Close</button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default PasswordResetModal;
